<template>
  <div id="app">
    <header class="app-header">
      <h1>课程选择系统</h1>
      <div v-if="isLoggedIn" class="user-info">
        <span>欢迎, {{ username }}</span>
        <button class="btn-logout" @click="logout">登出</button>
      </div>
    </header>

    <!-- 全局通知组件 -->
    <div v-if="notification.show" :class="['notification', notification.type]">
      <span>{{ notification.message }}</span>
      <button @click="closeNotification">×</button>
    </div>

    <!-- 登录/注册表单 -->
    <div v-if="!isLoggedIn" class="auth-container">
      <div class="auth-tabs">
        <button 
          :class="['tab-btn', { active: showLoginForm }]" 
          @click="showLoginForm = true">登录</button>
        <button 
          :class="['tab-btn', { active: !showLoginForm && registerType === 'student' }]" 
          @click="{ showLoginForm = false; registerType = 'student' }">学生注册</button>
        <button 
          :class="['tab-btn', { active: !showLoginForm && registerType === 'teacher' }]" 
          @click="{ showLoginForm = false; registerType = 'teacher' }">教师注册</button>
      </div>

      <div v-if="showLoginForm" class="auth-form">
        <h2>登录账户</h2>
        <form @submit.prevent="login">
          <div class="form-group">
            <label for="login-username">用户名</label>
            <input 
              id="login-username"
              v-model="loginForm.username" 
              type="text" 
              placeholder="请输入用户名" 
              required
              autocomplete="username">
          </div>
          <div class="form-group">
            <label for="login-password">密码</label>
            <input 
              id="login-password"
              v-model="loginForm.password" 
              type="password" 
              placeholder="请输入密码" 
              required
              autocomplete="current-password">
          </div>
          <button class="btn-primary" type="submit" :disabled="isLoading">
            {{ isLoading ? '登录中...' : '登录' }}
          </button>
        </form>
      </div>

      <div v-else class="auth-form">
        <h2>{{ registerType === 'student' ? '学生注册' : '教师注册' }}</h2>
        <form @submit.prevent="register">
          <div class="form-group">
            <label for="register-username">用户名</label>
            <input 
              id="register-username"
              v-model="registerForm.username" 
              type="text" 
              placeholder="请设置用户名" 
              required>
            <span class="form-hint">用户名将用于登录系统</span>
          </div>
          <div class="form-group">
            <label for="register-password">密码</label>
            <input 
              id="register-password"
              v-model="registerForm.password" 
              type="password" 
              placeholder="请设置密码" 
              required>
            <span class="form-hint">密码长度至少6位</span>
          </div>
          
          <!-- 学生特有字段 -->
          <div v-if="registerType === 'student'">
            <div class="form-group">
              <label for="register-student-id">学号</label>
              <input 
                id="register-student-id"
                v-model="registerForm.student_id" 
                type="text" 
                placeholder="请输入学号" 
                required>
            </div>
          </div>
          
          <!-- 教师特有字段 -->
          <div v-if="registerType === 'teacher'">
            <div class="form-group">
              <label for="register-teacher-id">教师工号</label>
              <input 
                id="register-teacher-id"
                v-model="registerForm.teacher_id" 
                type="text" 
                placeholder="请输入教师工号" 
                required>
            </div>
            <div class="form-group">
              <label for="register-department">所属院系</label>
              <input 
                id="register-department"
                v-model="registerForm.department" 
                type="text" 
                placeholder="请输入所属院系">
            </div>
            <div class="form-group">
              <label for="register-email">电子邮箱</label>
              <input 
                id="register-email"
                v-model="registerForm.email" 
                type="email" 
                placeholder="请输入电子邮箱">
            </div>
          </div>
          
          <div class="form-group">
            <label for="register-name">姓名</label>
            <input 
              id="register-name"
              v-model="registerForm.name" 
              type="text" 
              placeholder="请输入真实姓名" 
              required>
          </div>
          <button class="btn-primary" type="submit" :disabled="isLoading">
            {{ isLoading ? '注册中...' : '注册' }}
          </button>
        </form>
      </div>
    </div>

    <!-- 登录后的主内容区 -->
    <div v-else class="main-content">
      <!-- 教师功能区 -->
      <div v-if="isTeacher" class="teacher-panel">
        <h2>教师控制面板</h2>
        <div class="teacher-tabs">
          <button 
            :class="['teacher-tab-btn', { active: teacherActiveTab === 'courses' }]" 
            @click="teacherActiveTab = 'courses'">我的课程</button>
          <button 
            :class="['teacher-tab-btn', { active: teacherActiveTab === 'students' }]" 
            @click="teacherActiveTab = 'students'">学生管理</button>
        </div>

        <!-- 教师课程管理面板 -->
        <div v-if="teacherActiveTab === 'courses'" class="teacher-content">
          <button class="btn-secondary" @click="showAddCourseForm = !showAddCourseForm">
            {{ showAddCourseForm ? '取消' : '添加课程' }}
          </button>
          
          <div v-if="showAddCourseForm" class="teacher-form">
            <h3>添加新课程</h3>
            <form @submit.prevent="teacherAddCourse">
              <div class="form-group">
                <label for="add-teacher-course-id">课程编号</label>
                <input 
                  id="add-teacher-course-id"
                  v-model="teacherCourseForm.course_id" 
                  type="text" 
                  placeholder="课程编号" 
                  required>
              </div>
              <div class="form-group">
                <label for="add-teacher-course-name">课程名称</label>
                <input 
                  id="add-teacher-course-name"
                  v-model="teacherCourseForm.name" 
                  type="text" 
                  placeholder="课程名称" 
                  required>
              </div>
              <div class="form-group">
                <label for="add-teacher-course-description">课程描述</label>
                <textarea 
                  id="add-teacher-course-description"
                  v-model="teacherCourseForm.description" 
                  placeholder="课程描述"
                  rows="3">
                </textarea>
              </div>
              <div class="form-row">
                <div class="form-group half">
                  <label for="add-teacher-course-credits">学分</label>
                  <input 
                    id="add-teacher-course-credits"
                    v-model="teacherCourseForm.credits" 
                    type="number" 
                    min="1" 
                    placeholder="学分" 
                    required>
                </div>
                <div class="form-group half">
                  <label for="add-teacher-course-capacity">课程容量</label>
                  <input 
                    id="add-teacher-course-capacity"
                    v-model="teacherCourseForm.capacity" 
                    type="number" 
                    min="1" 
                    placeholder="课程容量" 
                    required>
                </div>
              </div>
              <button class="btn-primary" type="submit" :disabled="isLoading">添加课程</button>
            </form>
          </div>
          
          <!-- 教师课程列表 -->
          <div class="course-list-teacher">
            <h3>我的课程 ({{ teacherCourses.length }})</h3>
            <div v-if="teacherCourses.length > 0" class="teacher-courses">
              <div v-for="course in teacherCourses" :key="course.course_id" class="teacher-course-card">
                <div class="course-info">
                  <h4>{{ course.name }} <span class="course-id-badge">{{ course.course_id }}</span></h4>
                  <p v-if="course.description">{{ course.description }}</p>
                  <div class="course-meta-row">
                    <span>学分: {{ course.credits }}</span>
                    <span>容量: {{ course.capacity }}</span>
                    <span>已选: {{ course.enrolled_count }}</span>
                    <span>剩余: {{ course.remaining_seats }}</span>
                  </div>
                </div>
                <div class="course-schedules">
                  <h5>课程时间 <button @click="showScheduleForm(course)" class="btn-small">添加时间</button></h5>
                  <div v-if="course.schedules && course.schedules.length > 0" class="schedule-list">
                    <div v-for="schedule in course.schedules" :key="schedule.id" class="schedule-item">
                      <span>周{{ formatDayOfWeek(schedule.day_of_week) }}: {{ schedule.start_time }}-{{ schedule.end_time }}</span>
                      <span v-if="schedule.location">{{ schedule.location }}</span>
                      <div class="schedule-actions">
                        <button @click="showEditScheduleForm(course, schedule)" class="btn-small btn-secondary">修改</button>
                        <button @click="deleteSchedule(course.course_id, schedule.id)" class="btn-small btn-danger">删除</button>
                      </div>
                    </div>
                  </div>
                  <div v-else class="empty-state small">
                    未设置课程时间
                  </div>
                </div>
                <div class="course-actions">
                  <button @click="viewCourseStudents(course)" class="btn-secondary">查看学生</button>
                  <button @click="confirmDeleteCourse(course)" class="btn-danger">删除课程</button>
                </div>
              </div>
            </div>
            <div v-else class="empty-state">
              您尚未添加任何课程
            </div>
          </div>
        </div>
        
        <!-- 学生管理面板 -->
        <div v-if="teacherActiveTab === 'students' && selectedCourse" class="teacher-content">
          <div class="breadcrumb">
            <a href="#" @click.prevent="selectedCourse = null">所有课程</a> &gt; 
            <span>{{ selectedCourse.name }} 的学生</span>
          </div>
          
          <div class="student-list">
            <h3>已选学生 ({{ courseStudents.length }})</h3>
            <table v-if="courseStudents.length > 0" class="student-table">
              <thead>
                <tr>
                  <th>学号</th>
                  <th>姓名</th>
                  <th>选课时间</th>
                  <th>状态</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="student in courseStudents" :key="student.student_id">
                  <td>{{ student.student_id }}</td>
                  <td>{{ student.name }}</td>
                  <td>{{ formatDate(student.enrolled_at) }}</td>
                  <td>{{ formatStatus(student.status) }}</td>
                </tr>
              </tbody>
            </table>
            <div v-else class="empty-state">
              暂无学生选择此课程
            </div>
          </div>
        </div>
        
        <div v-if="teacherActiveTab === 'students' && !selectedCourse" class="teacher-content">
          <div class="empty-state">
            请在"我的课程"页面中选择课程查看学生
          </div>
        </div>
      </div>
      
      <!-- 管理员功能区 -->
      <div v-else-if="isAdmin" class="admin-panel">
        <h2>管理员控制面板</h2>
        <div class="admin-tabs">
          <button 
            :class="['admin-tab-btn', { active: adminActiveTab === 'students' }]" 
            @click="adminActiveTab = 'students'">学生管理</button>
          <button 
            :class="['admin-tab-btn', { active: adminActiveTab === 'courses' }]" 
            @click="adminActiveTab = 'courses'">课程管理</button>
          <button 
            :class="['admin-tab-btn', { active: adminActiveTab === 'stats' }]" 
            @click="adminActiveTab = 'stats'">系统统计</button>
        </div>

        <!-- 学生管理面板 -->
        <div v-if="adminActiveTab === 'students'" class="admin-content">
          <button class="btn-secondary" @click="showAddStudentForm = !showAddStudentForm">
            {{ showAddStudentForm ? '取消' : '添加学生' }}
          </button>
          
          <div v-if="showAddStudentForm" class="admin-form">
            <h3>添加新学生</h3>
            <form @submit.prevent="addStudent">
              <div class="form-group">
                <label for="add-student-id">学号</label>
                <input 
                  id="add-student-id"
                  v-model="addStudentForm.student_id" 
                  type="text" 
                  placeholder="学号" 
                  required>
              </div>
              <div class="form-group">
                <label for="add-student-name">姓名</label>
                <input 
                  id="add-student-name"
                  v-model="addStudentForm.name" 
                  type="text" 
                  placeholder="姓名" 
                  required>
              </div>
              <button class="btn-primary" type="submit" :disabled="isLoading">添加学生</button>
            </form>
          </div>
          
          <!-- 可以添加学生列表和其他管理功能 -->
        </div>

        <!-- 课程管理面板 -->
        <div v-if="adminActiveTab === 'courses'" class="admin-content">
          <button class="btn-secondary" @click="showAddCourseForm = !showAddCourseForm">
            {{ showAddCourseForm ? '取消' : '添加课程' }}
          </button>
          
          <div v-if="showAddCourseForm" class="admin-form">
            <h3>添加新课程</h3>
            <form @submit.prevent="addCourse">
              <div class="form-group">
                <label for="add-course-id">课程编号</label>
                <input 
                  id="add-course-id"
                  v-model="addCourseForm.course_id" 
                  type="text" 
                  placeholder="课程编号" 
                  required>
              </div>
              <div class="form-group">
                <label for="add-course-name">课程名称</label>
                <input 
                  id="add-course-name"
                  v-model="addCourseForm.name" 
                  type="text" 
                  placeholder="课程名称" 
                  required>
              </div>
              <div class="form-group">
                <label for="add-course-instructor">授课教师</label>
                <input 
                  id="add-course-instructor"
                  v-model="addCourseForm.instructor" 
                  type="text" 
                  placeholder="授课教师" 
                  required>
              </div>
              <div class="form-row">
                <div class="form-group half">
                  <label for="add-course-credits">学分</label>
                  <input 
                    id="add-course-credits"
                    v-model="addCourseForm.credits" 
                    type="number" 
                    min="1" 
                    placeholder="学分" 
                    required>
                </div>
                <div class="form-group half">
                  <label for="add-course-capacity">课程容量</label>
                  <input 
                    id="add-course-capacity"
                    v-model="addCourseForm.capacity" 
                    type="number" 
                    min="1" 
                    placeholder="课程容量" 
                    required>
                </div>
              </div>
              <button class="btn-primary" type="submit" :disabled="isLoading">添加课程</button>
            </form>
          </div>
          
          <!-- 课程列表 -->
          <div class="course-list-admin">
            <h3>所有课程 ({{ courses.length }})</h3>
            <table v-if="courses.length > 0" class="course-table">
              <thead>
                <tr>
                  <th>课程编号</th>
                  <th>课程名称</th>
                  <th>授课教师</th>
                  <th>学分</th>
                  <th>容量</th>
                  <th>剩余名额</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="course in courses" :key="course.course_id">
                  <td>{{ course.course_id }}</td>
                  <td>{{ course.name }}</td>
                  <td>{{ course.instructor }}</td>
                  <td>{{ course.credits }}</td>
                  <td>{{ course.capacity }}</td>
                  <td>
                    <span :class="{'text-danger': course.remaining_seats <= 0}">
                      {{ course.remaining_seats }}
                    </span>
                  </td>
                  <td>
                    <button class="btn-danger btn-small" @click="confirmRemoveCourse(course)">删除</button>
                  </td>
                </tr>
              </tbody>
            </table>
            <div v-else class="empty-state">
              暂无课程数据
            </div>
          </div>
        </div>

        <!-- 系统统计面板 -->
        <div v-if="adminActiveTab === 'stats'" class="admin-content">
          <h3>系统统计</h3>
          <div class="stats-cards">
            <div class="stat-card">
              <div class="stat-title">总课程数</div>
              <div class="stat-value">{{ courses.length }}</div>
            </div>
            <div class="stat-card">
              <div class="stat-title">学生选课数</div>
              <div class="stat-value">{{ enrolledCoursesCount }}</div>
            </div>
            <!-- 这里可以添加更多统计卡片 -->
          </div>
        </div>
      </div>

      <!-- 学生课程选择区 -->
      <div v-if="!isTeacher" class="student-content">
        <h2>课程选择</h2>
        
        <!-- 课程搜索 -->
        <div class="course-search">
          <input 
            v-model="courseSearch" 
            type="text" 
            placeholder="搜索课程名称或教师..."
            @input="filterCourses">
        </div>
        
        <div class="course-container">
          <!-- 可选课程列表 -->
          <div class="course-section">
            <h3>可选课程 <span class="badge">{{ filteredCourses.length }}</span></h3>
            <div class="course-grid">
              <div 
                v-for="course in filteredCourses" 
                :key="course.course_id" 
                class="course-card"
                :class="{ 'course-full': course.remaining_seats <= 0 }">
                <div class="course-header">
                  <h4 class="course-title">{{ course.name }}</h4>
                  <span class="course-id">{{ course.course_id }}</span>
                </div>
                <div class="course-body">
                  <p class="instructor"><i class="icon-teacher"></i> {{ course.instructor }}</p>
                  <div class="course-meta">
                    <span class="credits"><i class="icon-book"></i> {{ course.credits }} 学分</span>
                    <span 
                      class="seats" 
                      :class="{'text-danger': course.remaining_seats <= 0}">
                      <i class="icon-seat"></i> 剩余 {{ course.remaining_seats }} 座
                    </span>
                  </div>
                </div>
                <div class="course-footer">
                  <button 
                    @click="enroll(course.course_id)" 
                    :disabled="course.remaining_seats <= 0 || isLoading"
                    class="btn-enroll">
                    {{ course.remaining_seats <= 0 ? '已满' : '选课' }}
                  </button>
                </div>
              </div>
              
              <!-- 无课程时显示 -->
              <div v-if="filteredCourses.length === 0" class="empty-state">
                {{ courseSearch ? '没有找到匹配的课程' : '暂无可选课程' }}
              </div>
            </div>
          </div>

          <!-- 已选课程列表 -->
          <div class="enrolled-section">
            <h3>已选课程 <span class="badge">{{ enrolledCourses.length }}</span></h3>
            <div class="enrolled-list">
              <div 
                v-for="course in enrolledCourses" 
                :key="course.course_id" 
                class="enrolled-item">
                <div class="enrolled-info">
                  <h4>{{ course.name }}</h4>
                  <p>{{ course.instructor }} | {{ course.credits }} 学分</p>
                </div>
                <button 
                  @click="confirmDrop(course)"
                  :disabled="isLoading"
                  class="btn-drop">
                  退课
                </button>
              </div>
              
              <!-- 无已选课程时显示 -->
              <div v-if="enrolledCourses.length === 0" class="empty-state">
                您还没有选择任何课程
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 确认对话框 -->
    <div v-if="confirmDialog.show" class="confirm-dialog-backdrop">
      <div class="confirm-dialog">
        <h3>{{ confirmDialog.title }}</h3>
        <p class="dialog-message" v-html="confirmDialog.message.replace(/\n/g, '<br>')"></p>
        
        <!-- 课程时间表单 -->
        <div v-if="isEditingSchedule || scheduleForm.course_id" class="dialog-form">
          <div class="form-group">
            <label>星期</label>
            <select v-if="isEditingSchedule" v-model="editScheduleForm.day_of_week">
              <option value="1">星期一</option>
              <option value="2">星期二</option>
              <option value="3">星期三</option>
              <option value="4">星期四</option>
              <option value="5">星期五</option>
              <option value="6">星期六</option>
              <option value="7">星期日</option>
            </select>
            <select v-else v-model="scheduleForm.day_of_week">
              <option value="1">星期一</option>
              <option value="2">星期二</option>
              <option value="3">星期三</option>
              <option value="4">星期四</option>
              <option value="5">星期五</option>
              <option value="6">星期六</option>
              <option value="7">星期日</option>
            </select>
          </div>
          
          <div class="form-row">
            <div class="form-group half">
              <label>开始时间</label>
              <input 
                v-if="isEditingSchedule"
                v-model="editScheduleForm.start_time" 
                type="text" 
                placeholder="如：08:00 或 8:00am">
              <input 
                v-else
                v-model="scheduleForm.start_time" 
                type="text" 
                placeholder="如：08:00 或 8:00am">
              <span class="form-hint">支持多种时间格式</span>
            </div>
            
            <div class="form-group half">
              <label>结束时间</label>
              <input 
                v-if="isEditingSchedule"
                v-model="editScheduleForm.end_time" 
                type="text" 
                placeholder="如：09:40 或 9:40am">
              <input 
                v-else
                v-model="scheduleForm.end_time" 
                type="text" 
                placeholder="如：09:40 或 9:40am">
              <span class="form-hint">支持多种时间格式</span>
            </div>
          </div>
          
          <div class="form-group">
            <label>上课地点</label>
            <input 
              v-if="isEditingSchedule"
              v-model="editScheduleForm.location" 
              type="text" 
              placeholder="如：3号教学楼 304">
            <input 
              v-else
              v-model="scheduleForm.location" 
              type="text" 
              placeholder="如：3号教学楼 304">
          </div>
        </div>
        
        <div class="dialog-buttons">
          <button class="btn-secondary" @click="confirmDialog.show = false">取消</button>
          <button 
            class="btn-primary" 
            @click="confirmDialog.onConfirm">
            确认
          </button>
        </div>
      </div>
    </div>

    <!-- 加载指示器 -->
    <div v-if="isLoading" class="loading-overlay">
      <div class="loading-spinner"></div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

const api = axios.create({
  baseURL: 'http://localhost:5000',
  withCredentials: true,
  timeout: 10000, // 10秒超时
  headers: {
    'Content-Type': 'application/json'
  }
});

// API请求拦截器 - 添加加载状态
api.interceptors.request.use(config => {
  // 可以在此处理请求前的逻辑，例如设置加载状态
  return config;
}, error => {
  return Promise.reject(error);
});

// API响应拦截器 - 统一错误处理
api.interceptors.response.use(response => {
  return response;
}, error => {
  // 处理响应错误
  if (error.response) {
    // 服务器返回错误状态码
    if (error.response.status === 401) {
      // 身份验证错误，可以在这里处理登出逻辑
      console.error('Authentication error:', error.response.data);
    } else if (error.response.status === 429) {
      // 请求频率限制
      console.error('Rate limited:', error.response.data);
    }
  } else if (error.request) {
    // 请求发送但没有收到响应
    console.error('Network error:', error.request);
  } else {
    // 设置请求时发生错误
    console.error('Request error:', error.message);
  }
  return Promise.reject(error);
});

export default {
  data() {
    return {
      // 数据
      courses: [],
      enrolledCourses: [],
      filteredCourses: [],
      courseSearch: '',
      teacherCourses: [],
      courseStudents: [],
      selectedCourse: null,
      
      // 用户状态
      isLoggedIn: false,
      isAdmin: false,
      isTeacher: false,
      username: '',
      studentId: '',
      teacherId: '',
      teacherName: '',
      department: '',
      
      // UI状态
      isLoading: false,
      showLoginForm: true,
      registerType: 'student', // 'student' 或 'teacher'
      showAddStudentForm: false,
      showAddCourseForm: false,
      adminActiveTab: 'courses', // 'students', 'courses', 'stats'
      teacherActiveTab: 'courses', // 'courses', 'students'
      
      // 表单
      loginForm: {
        username: '',
        password: ''
      },
      registerForm: {
        username: '',
        password: '',
        student_id: '',
        teacher_id: '',
        name: '',
        department: '',
        email: '',
        user_type: 'student'
      },
      addStudentForm: {
        student_id: '',
        name: ''
      },
      addCourseForm: {
        course_id: '',
        name: '',
        instructor: '',
        credits: '',
        capacity: ''
      },
      teacherCourseForm: {
        course_id: '',
        name: '',
        description: '',
        credits: 3,
        capacity: 30
      },
      scheduleForm: {
        course_id: '',
        day_of_week: 1,
        start_time: '08:00',
        end_time: '09:40',
        location: '',
        time_format_help: '支持多种格式：14:30（24小时制）、2:30pm、2:30 pm（12小时制）'
      },
      editScheduleForm: {
        course_id: '',
        schedule_id: null,
        day_of_week: 1,
        start_time: '08:00',
        end_time: '09:40',
        location: '',
        time_format_help: '支持多种格式：14:30（24小时制）、2:30pm、2:30 pm（12小时制）'
      },
      isEditingSchedule: false,
      
      // 通知
      notification: {
        show: false,
        message: '',
        type: 'info' // 'info', 'success', 'warning', 'error'
      },
      
      // 确认对话框
      confirmDialog: {
        show: false,
        title: '',
        message: '',
        onConfirm: () => {}
      }
    };
  },
  
  computed: {
    enrolledCoursesCount() {
      return this.enrolledCourses.length;
    },
    
    // 根据当前编辑状态返回正确的表单对象
    currentScheduleForm() {
      return this.isEditingSchedule ? this.editScheduleForm : this.scheduleForm;
    }
  },
  
  created() {
    this.checkLoginStatus();
  },
  
  methods: {
    // ===== 通用方法 =====
    showNotification(message, type = 'info') {
      this.notification = {
        show: true,
        message,
        type
      };
      // 5秒后自动关闭
      setTimeout(() => {
        this.closeNotification();
      }, 5000);
    },
    
    closeNotification() {
      this.notification.show = false;
    },
    
    // ===== 认证方法 =====
    checkLoginStatus() {
      this.isLoading = true;
      api.get('/check_login')
        .then(response => {
          this.isLoggedIn = response.data.isLoggedIn;
          this.isAdmin = response.data.isAdmin;
          this.isTeacher = response.data.isTeacher;
          this.username = response.data.username;
          this.studentId = response.data.student_id;
          this.teacherId = response.data.teacher_id;
          this.teacherName = response.data.teacher_name;
          this.department = response.data.department;
          
          if (this.isLoggedIn) {
            if (this.isTeacher) {
              this.loadTeacherCourses();
            } else {
              this.loadCourses();
            }
          }
        })
        .catch(error => {
          console.error('Error checking login status:', error);
          this.isLoggedIn = false;
          this.isAdmin = false;
          this.isTeacher = false;
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    
  login() {
      if (!this.loginForm.username || !this.loginForm.password) {
        this.showNotification('请输入用户名和密码', 'warning');
        return;
      }
      
      this.isLoading = true;
      
      // 采用表单格式发送，解决后端解析问题
      const formData = new FormData();
      formData.append('username', this.loginForm.username);
      formData.append('password', this.loginForm.password);
      
      api.post('/login', formData)
        .then(response => {
          if (response.data.success) {
            this.isLoggedIn = true;
            this.isAdmin = response.data.isAdmin;
            this.isTeacher = response.data.isTeacher;
            this.username = response.data.username;
            this.studentId = response.data.student_id;
            this.teacherId = response.data.teacher_id;
            this.teacherName = response.data.teacher_name;
            this.department = response.data.department;
            
            if (this.isTeacher) {
              this.loadTeacherCourses();
            } else {
              this.loadCourses();
            }
            this.showNotification('登录成功', 'success');
          } else {
            this.showNotification(response.data.message || '登录失败', 'error');
          }
        })
        .catch(error => {
          console.error('登录错误:', error);
          if (error.response) {
            if (error.response.status === 401) {
              this.showNotification('用户名或密码错误', 'error');
            } else if (error.response.status === 400) {
              this.showNotification('请输入用户名和密码', 'warning');
            } else {
              this.showNotification('登录失败: ' + (error.response.data.message || '未知错误'), 'error');
            }
          } else if (error.request) {
            this.showNotification('无法连接到服务器，请检查网络连接', 'error');
          } else {
            this.showNotification('登录过程中发生错误，请稍后重试', 'error');
          }
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    
    register() {
      // 设置用户类型
      this.registerForm.user_type = this.registerType;
      
      // 验证表单
      if (!this.registerForm.username || !this.registerForm.password || !this.registerForm.name) {
        this.showNotification('请填写所有必填字段', 'warning');
        return;
      }
      
      if (this.registerType === 'student' && !this.registerForm.student_id) {
        this.showNotification('请填写学号', 'warning');
        return;
      }
      
      if (this.registerType === 'teacher' && !this.registerForm.teacher_id) {
        this.showNotification('请填写教师工号', 'warning');
        return;
      }
      
      if (this.registerForm.password.length < 6) {
        this.showNotification('密码长度不能少于6个字符', 'warning');
        return;
      }
      
      this.isLoading = true;
      api.post('/register', this.registerForm)
        .then(response => {
          if (response.data.success) {
            this.showNotification('注册成功！', 'success');
            this.loginForm = {
              username: this.registerForm.username,
              password: this.registerForm.password
            };
            this.showLoginForm = true;
            // 清空注册表单
            this.registerForm = {
              username: '',
              password: '',
              student_id: '',
              teacher_id: '',
              name: '',
              department: '',
              email: '',
              user_type: 'student'
            };
          } else {
            this.showNotification(response.data.message || '注册失败', 'error');
          }
        })
        .catch(error => {
          console.error('Registration error:', error);
          if (error.response && error.response.data) {
            this.showNotification('注册失败: ' + error.response.data.message, 'error');
          } else {
            this.showNotification('注册失败，请稍后重试', 'error');
          }
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    
    logout() {
      this.isLoading = true;
      api.post('/logout')
        .then(response => {
          console.log('登出响应:', response);
          
          // 无论服务器响应如何，始终清除本地状态
          this.isLoggedIn = false;
          this.isAdmin = false;
          this.isTeacher = false;
          this.username = '';
          this.studentId = '';
          this.teacherId = '';
          this.teacherName = '';
          this.department = '';
          this.courses = [];
          this.enrolledCourses = [];
          this.filteredCourses = [];
          this.teacherCourses = [];
          this.courseStudents = [];
          this.selectedCourse = null;
          
          // 清除本地存储中的任何认证数据
          localStorage.removeItem('authToken');
          localStorage.removeItem('userInfo');
          
          // 清除所有cookie
          document.cookie.split(';').forEach(cookie => {
            const [name] = cookie.trim().split('=');
            document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
          });
          
          if (response.data && response.data.success) {
            this.showNotification('已成功登出', 'success');
          } else {
            // 即使服务器返回失败，我们也认为本地登出成功
            this.showNotification('已登出系统', 'success');
          }
          
          // 刷新页面以确保所有状态都被重置
          // window.location.reload();
        })
        .catch(error => {
          console.error('登出错误:', error);
          
          // 即使API调用失败，我们仍然进行本地登出
          this.isLoggedIn = false;
          this.isAdmin = false;
          this.username = '';
          this.studentId = '';
          this.courses = [];
          this.enrolledCourses = [];
          this.filteredCourses = [];
          
          this.showNotification('登出过程中出现问题，但已成功退出系统', 'warning');
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    
    // ===== 课程管理 =====
    loadCourses() {
      this.isLoading = true;
      
      // 并行加载可选课程和已选课程
      Promise.all([
        api.get('/courses'),
        api.get('/student_courses')
      ])
        .then(([availableRes, enrolledRes]) => {
          this.courses = availableRes.data;
          this.enrolledCourses = enrolledRes.data;
          // 应用过滤器，排除已选课程
          this.filterCourses();
        })
        .catch(error => {
          console.error('Error loading courses:', error);
          if (error.response && error.response.status === 401) {
            this.isLoggedIn = false;
            this.showNotification('请先登录', 'warning');
          } else {
            this.showNotification('加载课程失败，请稍后重试', 'error');
          }
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    
    filterCourses() {
      // 确保数据是数组
      if (!Array.isArray(this.courses) || !Array.isArray(this.enrolledCourses)) {
        this.filteredCourses = [];
        return;
      }
      
      // 首先过滤掉已选的课程
      const availableCourses = this.courses.filter(course => {
        // 检查这门课是否已经在已选课程中
        return !this.enrolledCourses.some(enrolled => enrolled.course_id === course.course_id);
      });
      
      // 然后再根据搜索词进行过滤
      if (!this.courseSearch || !this.courseSearch.trim()) {
        this.filteredCourses = [...availableCourses];
        return;
      }
      
      const search = this.courseSearch.toLowerCase();
      this.filteredCourses = availableCourses.filter(course => 
        (course.name && course.name.toLowerCase().includes(search)) || 
        (course.instructor && course.instructor.toLowerCase().includes(search)) ||
        (course.course_id && course.course_id.toLowerCase().includes(search))
      );
    },
    
    enroll(courseId) {
      this.isLoading = true;
      api.post('/enroll', { course_id: courseId })
        .then(response => {
          if (response.data.success) {
            this.showNotification(response.data.message, 'success');
            this.loadCourses(); // 重新加载课程数据
          } else {
            this.showNotification(response.data.message, 'warning');
          }
        })
        .catch(error => {
          console.error('选课错误:', error);
          if (error.response && error.response.status === 401) {
            this.isLoggedIn = false;
            this.showNotification('请先登录', 'warning');
          } else {
            this.showNotification('选课失败: ' + (error.response?.data?.message || '未知错误'), 'error');
          }
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    
    confirmDrop(course) {
      this.confirmDialog = {
        show: true,
        title: '确认退课',
        message: `您确定要退选"${course.name}"课程吗？`,
        onConfirm: () => {
          this.drop(course.course_id);
          this.confirmDialog.show = false;
        }
      };
    },
    
    drop(courseId) {
      this.isLoading = true;
      api.post('/drop', { course_id: courseId })
        .then(response => {
          if (response.data.success) {
            this.showNotification(response.data.message, 'success');
            this.loadCourses(); // 重新加载课程数据
          } else {
            this.showNotification(response.data.message, 'warning');
          }
        })
        .catch(error => {
          console.error('退课错误:', error);
          if (error.response && error.response.status === 401) {
            this.isLoggedIn = false;
            this.showNotification('请先登录', 'warning');
          } else {
            this.showNotification('退课失败: ' + (error.response?.data?.message || '未知错误'), 'error');
          }
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    
    // ===== 管理员功能 =====
    addStudent() {
      if (!this.addStudentForm.student_id || !this.addStudentForm.name) {
        this.showNotification('请填写所有必填字段', 'warning');
        return;
      }
      
      this.isLoading = true;
      api.post('/add_student', this.addStudentForm)
        .then(response => {
          if (response.data.success) {
            this.showNotification('学生添加成功', 'success');
            this.addStudentForm = { student_id: '', name: '' };
            this.showAddStudentForm = false;
          } else {
            this.showNotification(response.data.message || '添加学生失败', 'error');
          }
        })
        .catch(error => {
          console.error('添加学生错误:', error);
          this.showNotification('添加学生失败: ' + (error.response?.data?.message || '未知错误'), 'error');
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    
    addCourse() {
      // 验证表单
      if (!this.addCourseForm.course_id || !this.addCourseForm.name || 
          !this.addCourseForm.instructor || !this.addCourseForm.credits || 
          !this.addCourseForm.capacity) {
        this.showNotification('请填写所有必填字段', 'warning');
        return;
      }
      
      this.isLoading = true;
      api.post('/add_course', this.addCourseForm)
        .then(response => {
          if (response.data.success) {
            this.showNotification('课程添加成功', 'success');
            this.addCourseForm = { 
              course_id: '', 
              name: '', 
              instructor: '', 
              credits: '', 
              capacity: '' 
            };
            this.showAddCourseForm = false;
            this.loadCourses(); // 重新加载课程列表
          } else {
            this.showNotification(response.data.message || '添加课程失败', 'error');
          }
        })
        .catch(error => {
          console.error('添加课程错误:', error);
          this.showNotification('添加课程失败: ' + (error.response?.data?.message || '未知错误'), 'error');
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    
    confirmRemoveCourse(course) {
      this.confirmDialog = {
        show: true,
        title: '确认删除课程',
        message: `您确定要删除"${course.name}"课程吗？此操作不可撤销，将删除所有学生对该课程的选课记录。`,
        onConfirm: () => {
          this.removeCourse(course.course_id);
          this.confirmDialog.show = false;
        }
      };
    },
    
    removeCourse(courseId) {
      this.isLoading = true;
      api.post('/remove_course', { course_id: courseId })
        .then(response => {
          if (response.data.success) {
            this.showNotification('课程删除成功', 'success');
            this.loadCourses(); // 重新加载课程列表
          } else {
            this.showNotification(response.data.message || '删除课程失败', 'error');
          }
        })
        .catch(error => {
          console.error('删除课程错误:', error);
          this.showNotification('删除课程失败: ' + (error.response?.data?.message || '未知错误'), 'error');
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    
    // ===== 教师功能 =====
    loadTeacherCourses() {
      this.isLoading = true;
      api.get('/teacher/courses')
        .then(response => {
          if (response.data.success) {
            this.teacherCourses = response.data.courses || [];
          } else {
            this.showNotification('获取课程列表失败', 'warning');
            this.teacherCourses = [];
          }
        })
        .catch(error => {
          console.error('获取教师课程错误:', error);
          this.showNotification('获取课程列表失败', 'error');
          this.teacherCourses = [];
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    
    teacherAddCourse() {
      // 验证表单
      if (!this.teacherCourseForm.course_id || !this.teacherCourseForm.name || 
          !this.teacherCourseForm.credits || !this.teacherCourseForm.capacity) {
        this.showNotification('请填写所有必填字段', 'warning');
        return;
      }
      
      this.isLoading = true;
      api.post('/teacher/add_course', this.teacherCourseForm)
        .then(response => {
          if (response.data.success) {
            this.showNotification('课程添加成功', 'success');
            this.teacherCourseForm = { 
              course_id: '', 
              name: '', 
              description: '', 
              credits: 3, 
              capacity: 30 
            };
            this.showAddCourseForm = false;
            this.loadTeacherCourses(); // 重新加载课程列表
          } else {
            this.showNotification(response.data.message || '添加课程失败', 'error');
          }
        })
        .catch(error => {
          console.error('添加课程错误:', error);
          this.showNotification('添加课程失败: ' + (error.response?.data?.message || '未知错误'), 'error');
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    
    showScheduleForm(course) {
      this.isEditingSchedule = false;
      this.scheduleForm = {
        course_id: course.course_id,
        day_of_week: 1,
        start_time: '08:00',
        end_time: '09:40',
        location: ''
      };
      
      this.confirmDialog = {
        show: true,
        title: '添加课程时间',
        message: `为课程 "${course.name}" 添加时间安排`,
        onConfirm: () => {
          this.addCourseSchedule();
        }
      };
    },
    
    showEditScheduleForm(course, schedule) {
      this.isEditingSchedule = true;
      this.editScheduleForm = {
        course_id: course.course_id,
        schedule_id: schedule.id,
        day_of_week: schedule.day_of_week,
        start_time: schedule.start_time,
        end_time: schedule.end_time,
        location: schedule.location || ''
      };
      
      this.confirmDialog = {
        show: true,
        title: '修改课程时间',
        message: `修改课程 "${course.name}" 的时间安排`,
        onConfirm: () => {
          this.updateCourseSchedule();
        }
      };
    },
    
    addCourseSchedule() {
      // 验证表单
      if (!this.scheduleForm.day_of_week || !this.scheduleForm.start_time || !this.scheduleForm.end_time) {
        this.showNotification('请填写所有必填字段', 'warning');
        return;
      }
      
      this.isLoading = true;
      api.post(`/teacher/course/${this.scheduleForm.course_id}/schedule`, this.scheduleForm)
        .then(response => {
          if (response.data.success) {
            this.showNotification('课程时间添加成功', 'success');
            this.confirmDialog.show = false;
            this.loadTeacherCourses(); // 重新加载课程列表
          } else {
            this.showNotification(response.data.message || '添加课程时间失败', 'error');
          }
        })
        .catch(error => {
          console.error('添加课程时间错误:', error);
          this.showNotification('添加课程时间失败: ' + (error.response?.data?.message || '未知错误'), 'error');
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    
    updateCourseSchedule() {
      // 验证表单
      if (!this.editScheduleForm.day_of_week || !this.editScheduleForm.start_time || !this.editScheduleForm.end_time) {
        this.showNotification('请填写所有必填字段', 'warning');
        return;
      }
      
      this.isLoading = true;
      api.put(`/teacher/course/${this.editScheduleForm.course_id}/schedule/${this.editScheduleForm.schedule_id}`, this.editScheduleForm)
        .then(response => {
          if (response.data.success) {
            this.showNotification('课程时间修改成功', 'success');
            this.confirmDialog.show = false;
            this.loadTeacherCourses(); // 重新加载课程列表
          } else {
            this.showNotification(response.data.message || '修改课程时间失败', 'error');
          }
        })
        .catch(error => {
          console.error('修改课程时间错误:', error);
          this.showNotification('修改课程时间失败: ' + (error.response?.data?.message || '未知错误'), 'error');
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    
    deleteSchedule(courseId, scheduleId) {
      this.isLoading = true;
      api.delete(`/teacher/course/${courseId}/schedule/${scheduleId}`)
        .then(response => {
          if (response.data.success) {
            this.showNotification('课程时间删除成功', 'success');
            this.loadTeacherCourses(); // 重新加载课程列表
          } else {
            this.showNotification(response.data.message || '删除课程时间失败', 'error');
          }
        })
        .catch(error => {
          console.error('删除课程时间错误:', error);
          this.showNotification('删除课程时间失败: ' + (error.response?.data?.message || '未知错误'), 'error');
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    
    viewCourseStudents(course) {
      this.selectedCourse = course;
      this.teacherActiveTab = 'students';
      this.loadCourseStudents(course.course_id);
    },
    
    loadCourseStudents(courseId) {
      this.isLoading = true;
      api.get(`/teacher/course/${courseId}/students`)
        .then(response => {
          if (response.data.success) {
            this.courseStudents = response.data.students || [];
          } else {
            this.showNotification('获取学生列表失败', 'warning');
            this.courseStudents = [];
          }
        })
        .catch(error => {
          console.error('获取课程学生错误:', error);
          this.showNotification('获取学生列表失败', 'error');
          this.courseStudents = [];
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    
    confirmDeleteCourse(course) {
      this.confirmDialog = {
        show: true,
        title: '确认删除课程',
        message: `您确定要删除"${course.name}"课程吗？此操作不可撤销。`,
        onConfirm: () => {
          this.deleteCourse(course.course_id);
          this.confirmDialog.show = false;
        }
      };
    },
    
    deleteCourse(courseId) {
      this.isLoading = true;
      api.delete(`/teacher/course/${courseId}`)
        .then(response => {
          if (response.data.success) {
            this.showNotification('课程删除成功', 'success');
            this.loadTeacherCourses(); // 重新加载课程列表
          } else {
            this.showNotification(response.data.message || '删除课程失败', 'error');
          }
        })
        .catch(error => {
          console.error('删除课程错误:', error);
          this.showNotification('删除课程失败: ' + (error.response?.data?.message || '未知错误'), 'error');
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    
    formatDayOfWeek(day) {
      // 修复星期几显示，数组下标从1开始，避免日期显示重复
      const days = ['', '一', '二', '三', '四', '五', '六', '日'];
      return days[day] || day;
    },
    
    formatDate(dateString) {
      if (!dateString) return '未知';
      try {
        const date = new Date(dateString);
        // 检查是否为有效日期
        if (isNaN(date.getTime())) {
          return '格式错误';
        }
        return date.toLocaleString('zh-CN', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit'
        });
      } catch (e) {
        console.error('日期格式化错误:', e);
        return '格式错误';
      }
    },
    
    formatStatus(status) {
      const statusMap = {
        'enrolled': '已选',
        'dropped': '已退',
        'waitlisted': '候补'
      };
      return statusMap[status] || status;
    }
  }
};
</script>

<style>
:root {
  --primary-color: #1e3a8a;
  --secondary-color: #10b981;
  --accent-color: #3b82f6;
  --danger-color: #ef4444;
  --warning-color: #f59e0b;
  --success-color: #22c55e;
  --info-color: #3b82f6;
  --gray-100: #f3f4f6;
  --gray-200: #e5e7eb;
  --gray-300: #d1d5db;
  --gray-400: #9ca3af;
  --gray-500: #6b7280;
  --gray-600: #4b5563;
  --gray-700: #374151;
  --gray-800: #1f2937;
  --gray-900: #111827;
  --border-radius: 8px;
  --shadow-sm: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
  --shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  --shadow-lg: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  --spacing-unit: 8px;
  --font-sans: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
}

* {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

body {
  font-family: var(--font-sans);
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  background-color: var(--gray-100);
  color: var(--gray-800);
  line-height: 1.5;
}

#app {
  max-width: 1400px;
  margin: 0 auto;
  padding: 16px;
}

/* ===== 头部样式 ===== */
.app-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 0;
  margin-bottom: 32px;
  border-bottom: 1px solid var(--gray-200);
}

.app-header h1 {
  font-size: 24px;
  color: var(--primary-color);
  margin: 0;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 16px;
}

.user-info span {
  font-weight: 500;
  color: var(--gray-700);
}

.btn-logout {
  background-color: transparent;
  color: var(--gray-600);
  border: 1px solid var(--gray-300);
  padding: 8px 16px;
  border-radius: var(--border-radius);
  cursor: pointer;
  transition: all 0.2s;
}

.btn-logout:hover {
  background-color: var(--gray-100);
  color: var(--gray-800);
}

/* ===== 通知组件 ===== */
.notification {
  position: fixed;
  top: 16px;
  right: 16px;
  padding: 12px 16px;
  border-radius: var(--border-radius);
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: var(--shadow);
  z-index: 1000;
  max-width: 400px;
  animation: slideIn 0.3s ease;
}

@keyframes slideIn {
  from { transform: translateX(100%); opacity: 0; }
  to { transform: translateX(0); opacity: 1; }
}

.notification button {
  background: transparent;
  border: none;
  font-size: 18px;
  color: currentColor;
  opacity: 0.7;
  cursor: pointer;
  padding: 0 0 0 16px;
}

.notification.info { background-color: var(--info-color); color: white; }
.notification.success { background-color: var(--success-color); color: white; }
.notification.warning { background-color: var(--warning-color); color: white; }
.notification.error { background-color: var(--danger-color); color: white; }

/* ===== 认证表单 ===== */
.auth-container {
  max-width: 480px;
  margin: 48px auto;
  background: white;
  border-radius: var(--border-radius);
  box-shadow: var(--shadow);
  overflow: hidden;
}

.auth-tabs {
  display: flex;
  border-bottom: 1px solid var(--gray-200);
}

.tab-btn {
  flex: 1;
  padding: 16px;
  background: transparent;
  border: none;
  font-size: 16px;
  font-weight: 500;
  color: var(--gray-600);
  cursor: pointer;
  transition: all 0.2s;
}

.tab-btn.active {
  color: var(--primary-color);
  box-shadow: inset 0 -2px 0 var(--primary-color);
}

.auth-form {
  padding: 24px;
}

.auth-form h2 {
  margin-bottom: 24px;
  font-size: 20px;
  color: var(--gray-800);
  text-align: center;
}

.form-group {
  margin-bottom: 16px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: var(--gray-700);
  text-align: left;
}

.form-hint {
  display: block;
  margin-top: 4px;
  font-size: 12px;
  color: var(--gray-500);
  text-align: left;
}

.form-row {
  display: flex;
  gap: 16px;
}

.form-group.half {
  flex: 1;
}

input {
  width: 100%;
  padding: 12px;
  border: 1px solid var(--gray-300);
  border-radius: var(--border-radius);
  font-size: 14px;
  color: var(--gray-800);
  transition: all 0.2s;
}

input:focus {
  outline: none;
  border-color: var(--accent-color);
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.btn-primary {
  width: 100%;
  padding: 12px;
  margin-top: 16px;
  background-color: var(--primary-color);
  color: white;
  border: none;
  border-radius: var(--border-radius);
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-primary:hover {
  background-color: #1a365d;
  transform: translateY(-1px);
}

.btn-primary:disabled {
  background-color: var(--gray-400);
  cursor: not-allowed;
  transform: none;
}

/* ===== 主内容区 ===== */
.main-content {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

/* ===== 管理员面板 ===== */
.admin-panel {
  background: white;
  border-radius: var(--border-radius);
  box-shadow: var(--shadow);
  overflow: hidden;
  margin-bottom: 24px;
}

.admin-panel h2 {
  padding: 16px;
  margin: 0;
  font-size: 18px;
  color: var(--gray-800);
  border-bottom: 1px solid var(--gray-200);
}

.admin-tabs {
  display: flex;
  background-color: var(--gray-100);
  padding: 8px;
  border-bottom: 1px solid var(--gray-200);
}

.admin-tab-btn {
  flex: 1;
  padding: 8px 16px;
  background: transparent;
  border: none;
  font-size: 14px;
  color: var(--gray-600);
  border-radius: var(--border-radius);
  cursor: pointer;
  transition: all 0.2s;
}

.admin-tab-btn.active {
  background-color: white;
  color: var(--primary-color);
  box-shadow: var(--shadow-sm);
}

.admin-content {
  padding: 16px;
}

.btn-secondary {
  padding: 8px 16px;
  background-color: white;
  color: var(--primary-color);
  border: 1px solid var(--primary-color);
  border-radius: var(--border-radius);
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-secondary:hover {
  background-color: rgba(59, 130, 246, 0.05);
}

.admin-form {
  max-width: 600px;
  margin: 16px auto;
  padding: 16px;
  background-color: var(--gray-100);
  border-radius: var(--border-radius);
}

.course-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 16px;
  font-size: 14px;
}

.course-table th, .course-table td {
  padding: 12px;
  text-align: left;
  border-bottom: 1px solid var(--gray-200);
}

.course-table th {
  font-weight: 500;
  color: var(--gray-700);
  background-color: var(--gray-100);
}

.btn-danger {
  background-color: var(--danger-color);
  color: white;
  border: none;
  border-radius: var(--border-radius);
  padding: 8px 16px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-danger:hover {
  background-color: #b91c1c;
}

.btn-small {
  padding: 4px 8px;
  font-size: 12px;
  background-color: white;
  color: var(--primary-color);
  border: 1px solid var(--primary-color);
  border-radius: var(--border-radius);
  cursor: pointer;
  transition: all 0.2s;
}
.btn-small:hover {
  background-color: rgba(59, 130, 246, 0.05);
}

select {
  width: 100%;
  padding: 12px;
  border: 1px solid var(--gray-300);
  border-radius: var(--border-radius);
  font-size: 14px;
  color: var(--primary-color);
  transition: all 0.2s;
}

.text-danger {
  color: var(--danger-color);
  font-weight: 500;
}

.stats-cards {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 16px;
  margin-top: 16px;
}

.stat-card {
  background-color: white;
  padding: 16px;
  border-radius: var(--border-radius);
  box-shadow: var(--shadow-sm);
}

.stat-title {
  font-size: 14px;
  color: var(--gray-600);
  margin-bottom: 8px;
}

.stat-value {
  font-size: 24px;
  font-weight: 600;
  color: var(--primary-color);
}

/* ===== 教师面板 ===== */
.teacher-panel {
  background: white;
  border-radius: var(--border-radius);
  box-shadow: var(--shadow);
  overflow: hidden;
  margin-bottom: 24px;
}

.teacher-panel h2 {
  padding: 16px;
  margin: 0;
  font-size: 18px;
  color: var(--gray-800);
  border-bottom: 1px solid var(--gray-200);
}

.teacher-tabs {
  display: flex;
  background-color: var(--gray-100);
  padding: 8px;
  border-bottom: 1px solid var(--gray-200);
}

.teacher-tab-btn {
  flex: 1;
  padding: 8px 16px;
  background: transparent;
  border: none;
  font-size: 14px;
  color: var(--gray-600);
  border-radius: var(--border-radius);
  cursor: pointer;
  transition: all 0.2s;
}

.teacher-tab-btn.active {
  background-color: white;
  color: var(--primary-color);
  box-shadow: var(--shadow-sm);
}

.teacher-content {
  padding: 16px;
}

.teacher-form {
  max-width: 600px;
  margin: 16px auto;
  padding: 16px;
  background-color: var(--gray-100);
  border-radius: var(--border-radius);
}

.course-list-teacher {
  margin-top: 20px;
}

.teacher-courses {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 16px;
  margin-top: 16px;
}

.teacher-course-card {
  background-color: white;
  border: 1px solid var(--gray-200);
  border-radius: var(--border-radius);
  padding: 16px;
  box-shadow: var(--shadow-sm);
}

.teacher-course-card h4 {
  margin: 0 0 8px 0;
  font-size: 18px;
  color: var(--primary-color);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.course-id-badge {
  font-size: 12px;
  background-color: var(--gray-100);
  color: var(--gray-600);
  padding: 2px 6px;
  border-radius: 12px;
}

.course-meta-row {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin: 8px 0;
  font-size: 13px;
  color: var(--gray-600);
}

.course-schedules {
  margin: 16px 0;
  border-top: 1px solid var(--gray-200);
  padding-top: 12px;
}

.course-schedules h5 {
  margin: 0 0 8px 0;
  font-size: 14px;
  color: var(--gray-700);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.schedule-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.schedule-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px;
  background-color: var(--gray-50);
  border-radius: var(--border-radius);
  font-size: 13px;
}

.course-actions {
  display: flex;
  justify-content: space-between;
  margin-top: 16px;
  border-top: 1px solid var(--gray-200);
  padding-top: 16px;
}

.breadcrumb {
  margin-bottom: 16px;
  font-size: 14px;
  color: var(--gray-600);
}

.breadcrumb a {
  color: var(--primary-color);
  text-decoration: none;
}

.breadcrumb a:hover {
  text-decoration: underline;
}

.student-list {
  margin-top: 16px;
}

.student-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 16px;
  font-size: 14px;
}

.student-table th, .student-table td {
  padding: 12px;
  text-align: left;
  border-bottom: 1px solid var(--gray-200);
}

.student-table th {
  font-weight: 500;
  color: var(--gray-700);
  background-color: var(--gray-100);
}

.empty-state.small {
  padding: 12px;
  font-size: 13px;
}

textarea {
  width: 100%;
  padding: 12px;
  border: 1px solid var(--gray-300);
  border-radius: var(--border-radius);
  font-size: 14px;
  color: var(--gray-800);
  transition: all 0.2s;
  resize: vertical;
  font-family: var(--font-sans);
}

textarea:focus {
  outline: none;
  border-color: var(--accent-color);
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

/* ===== 学生界面 ===== */
.student-content h2 {
  margin-bottom: 16px;
  font-size: 20px;
  color: var(--gray-800);
}

.course-search {
  margin-bottom: 16px;
}

.course-search input {
  width: 100%;
  max-width: 400px;
  padding: 12px 16px;
  border: 1px solid var(--gray-300);
  border-radius: 24px;
  background-color: white;
  font-size: 14px;
}

.course-container {
  display: grid;
  grid-template-columns: 1fr 300px;
  gap: 16px;
}

.course-section, .enrolled-section {
  background: white;
  border-radius: var(--border-radius);
  box-shadow: var(--shadow);
  overflow: hidden;
}

.course-section h3, .enrolled-section h3 {
  padding: 16px;
  margin: 0;
  font-size: 16px;
  color: var(--gray-800);
  border-bottom: 1px solid var(--gray-200);
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.badge {
  background-color: var(--primary-color);
  color: white;
  font-size: 12px;
  padding: 2px 6px;
  border-radius: 12px;
}

.course-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 16px;
  padding: 16px;
}

.course-card {
  background-color: white;
  border: 1px solid var(--gray-200);
  border-radius: var(--border-radius);
  overflow: hidden;
  transition: all 0.2s;
}

.course-card:hover {
  transform: translateY(-2px);
  box-shadow: var(--shadow);
}

.course-header {
  padding: 12px;
  background-color: var(--gray-100);
  border-bottom: 1px solid var(--gray-200);
}

.course-title {
  margin: 0;
  font-size: 16px;
  font-weight: 500;
  color: var(--primary-color);
}

.course-id {
  font-size: 12px;
  color: var(--gray-500);
}

.course-body {
  padding: 12px;
}

.instructor {
  margin: 0 0 8px 0;
  font-size: 14px;
  color: var(--gray-700);
}

.course-meta {
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  color: var(--gray-600);
}

.course-footer {
  padding: 8px 12px;
  background-color: var(--gray-50);
  border-top: 1px solid var(--gray-200);
}

.btn-enroll {
  width: 100%;
  padding: 8px;
  background-color: var(--secondary-color);
  color: white;
  border: none;
  border-radius: var(--border-radius);
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-enroll:hover {
  background-color: #059669;
}

.btn-enroll:disabled {
  background-color: var(--gray-400);
  cursor: not-allowed;
}

.course-full {
  opacity: 0.7;
}

.enrolled-list {
  padding: 16px;
}

.enrolled-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  border-bottom: 1px solid var(--gray-200);
}

.enrolled-item:last-child {
  border-bottom: none;
}

.enrolled-info h4 {
  margin: 0;
  font-size: 16px;
  color: var(--gray-800);
}

.enrolled-info p {
  margin: 4px 0 0 0;
  font-size: 12px;
  color: var(--gray-600);
}

.btn-drop {
  padding: 6px 12px;
  background-color: var(--danger-color);
  color: white;
  border: none;
  border-radius: var(--border-radius);
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-drop:hover {
  background-color: #b91c1c;
}

.empty-state {
  padding: 32px;
  text-align: center;
  color: var(--gray-500);
  font-size: 14px;
}

/* ===== 确认对话框 ===== */
.confirm-dialog-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.confirm-dialog {
  background-color: white;
  border-radius: var(--border-radius);
  box-shadow: var(--shadow-lg);
  padding: 24px;
  width: 90%;
  max-width: 400px;
}

.confirm-dialog h3 {
  margin-top: 0;
  margin-bottom: 16px;
  font-size: 18px;
  color: var(--gray-800);
}

.dialog-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
}

/* ===== 加载指示器 ===== */
.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid var(--gray-200);
  border-top-color: var(--primary-color);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* ===== 响应式设计 ===== */
@media (max-width: 768px) {
  .course-container {
    grid-template-columns: 1fr;
  }
  
  .course-table th:nth-child(4),
  .course-table td:nth-child(4),
  .course-table th:nth-child(5),
  .course-table td:nth-child(5) {
    display: none;
  }
}
</style>
